import axios from 'axios';
import { apiErrorMessage } from '../api/abfApi';

/**
 * The collections close (FRD §12). A separate surface from the collateral cycle
 * on purpose: the collateral tab answers "what do we lend against" against the
 * determination date, this one answers "what arrived and who gets it" against
 * the payment date.
 */
const base = (facilityId: number) => `/api/credit/abf/collections/facilities/${facilityId}`;

export interface CalendarStop {
  key: string; label: string; date: string | null;
  state: 'PAST' | 'TODAY' | 'AHEAD' | 'UNKNOWN'; daysAway: number | null;
}

export interface Deliverable {
  id: number; kind: string; label: string;
  accountKey: string | null; accountType: string | null;
  dueDate: string | null; dueDateBasis: string; cadence: string;
  required: boolean; waived: boolean; waivedBy: string | null; waiverReason: string | null;
  state: 'RECEIVED' | 'EXPECTED' | 'LATE' | 'WAIVED' | 'NOT_REQUIRED';
  daysLate: number | null;
  sourceChannel?: string | null; sourceRef?: string | null; arrivedAt?: string | null;
  lineCount?: number | null;
  openingBalance?: number | null; closingBalance?: number | null; balanceAsOf?: string | null;
  /** Bank-statement deliverables: every file on record for the account and period. */
  files?: BankFileOnRecord[];
}

export interface BankFileOnRecord {
  id: number; name: string; line_count: number | null; control_total: number | null;
  created_at: string | null; created_by: string | null;
}

export interface AccountRow {
  id: number; accountKey: string; accountName: string | null; accountType: string;
  bankName: string | null; maskedAccountNumber: string | null; currency: string;
  authority: string; controlAgreement: string | null;
  sweepToAccountKey: string | null; sweepFrequency: string | null;
  openingBalance: number | null; closingBalance: number | null; balanceAsOf: string | null;
  credits: number; debits: number; collections: number; lineCount: number;
  feedStatus: 'CURRENT' | 'STALE' | 'NO_STATEMENTS' | 'NO_BALANCES'; feedNote: string | null;
  requiredBalance?: number | null; requiredBalancePct?: number | null;
  requiredBalanceBasis?: string | null; reserveShortfall?: number | null; reserveNote?: string | null;
}

export interface ReconcilingItem {
  id: number; item_class: 'TIMING' | 'BREAK' | 'EXPLAINED'; category: string; category_title?: string; direction: string;
  amount: number; currency: string; status: string;
  detection_basis: string | null; expected_evidence: string | null;
  disposition: string | null; disposition_notes: string | null;
  dispositioned_by: string | null; dispositioned_at: string | null;
  blocks_close: boolean; blocks_funding: boolean;
  correlation_key: string | null; correlation_note: string | null;
  revisions: Array<{
    disposition: string | null; disposition_label: string | null; disposition_notes: string | null;
    dispositioned_by: string | null; revised_by: string; revised_at: string; revision_reason: string;
  }>;
  loans: Array<{ asset_identifier: string; obligor_name: string | null; amount: number }>;
}

export interface RailStep { key: string; label: string; state: string; detail: string; }

export interface CollectionsPeriodChip {
  id: number; periodLabel: string; periodStart: string; periodEnd: string;
  determinationDate: string; paymentDate: string; deliverablesDueDate: string;
  authorityStatus: string; versionNo: number; stage: string; stageLabel: string;
  closedAt?: string | null; signedOffBy?: string | null; signedOffChecker?: string | null; signedOffAt?: string | null;
  deliverablesReceived: number; deliverablesRequired: number; deliverablesLate: number;
  openBreaks: number; carriedTimingItems: number; unexplained: number | null; daysToPayment: number;
}

export interface CollectionsWorkspace {
  facility: Record<string, any>;
  businessDate: string; wallClockDate: string; businessDateSource: 'EOD_ROLL' | 'MANUAL' | 'EXTERNAL_FEED';
  paymentCalendar: Record<string, any> | null;
  periods: CollectionsPeriodChip[];
  selectedPeriodId: number | null;
  readiness: { ready: boolean; blockers: Array<{ key: string; reason: string; remedy: string }> };
  unassignedEvidence: Record<string, any>;
  period?: {
    id: number; period_label: string; stage: string; stageLabel: string;
    /** WS7: the period's projection summary, when one exists. */
    expectedCollections?: Record<string, any> | null;
    period_start: string; period_end: string; determination_date: string; payment_date: string;
    status: string; version_no: number;
    signed_off_by: string | null; signed_off_checker: string | null; signed_off_at: string | null;
    calendarStrip: CalendarStop[];
    deliverables: Deliverable[];
    accountMap: {
      accounts: AccountRow[]; currency: string; periodCollections: number;
      sweepTransfers: number; unidentifiedCredits: number;
      collateralBasisBalance: number | null; collateralBasisTapeId: number | null;
      collateralBasisAsOf: string | null; sweepNote: string | null;
      collectionsNote?: string | null;
    };
    signals: Record<string, any>;
    cashSummary?: { matchedTotal: number; receivedByDetermination: number; receivedAfterDetermination: number; returns: number; afterDeterminationTransactionIds: number[]; creditCount: number };
    tolerances: Record<string, any>;
    statement: { run: Record<string, any> | null; items: ReconcilingItem[];
                 carriedIn: ReconcilingItem[]; unexplainedTotal: number };
    waterfallCheck: Record<string, any>;
    lenderCash: Record<string, any>;
    closeOutRail: RailStep[];
  };
}

export const getCollectionsWorkspace = async (facilityId: number, periodId?: number | null) =>
  (await axios.get<CollectionsWorkspace>(
    `${base(facilityId)}${periodId ? `?period=${periodId}` : ''}`)).data;

export interface BankLine {
  id: number; bank_transaction_id: string; value_date: string; direction: string;
  amount: number; currency: string; reference_text: string | null; counterparty: string | null;
  classification: string; classification_basis: string | null; classification_reason: string | null;
  classified_by: string | null; matched_amount: number; status: string;
  collection_period_id: number | null; sweep_pair_transaction_id: number | null;
  account_key: string; account_type: string; source_reference: string; lane: string;
}

export const getBankLines = async (facilityId: number, periodId: number, accountId?: number | null) =>
  (await axios.get<{ lines: BankLine[]; classifications: string[]; windowFrom: string;
                     windowTo: string; windowNote: string }>(
    `${base(facilityId)}/periods/${periodId}/bank-lines${accountId ? `?account=${accountId}` : ''}`)).data;

export const getFormats = async (facilityId: number) =>
  (await axios.get<Record<string, any>>(`${base(facilityId)}/formats`)).data;

export const checkOnRecord = async (facilityId: number, kind: string, fileHash: string) =>
  (await axios.post<Record<string, any>>(
    `${base(facilityId)}/evidence/on-record`, { kind, fileHash })).data;

export interface BankEvidencePayload {
  facilityAccountId: number; lane: 'MT940' | 'MT942' | 'CSV' | 'KEYED';
  sourceReference?: string; sourceFilename?: string; currency?: string;
  rawMessage?: string; csv?: string;
  lines?: Array<Record<string, any>>;
  openingBalance?: number | null; closingBalance?: number | null;
  balanceAsOf?: string | null; statementFrom?: string | null; statementTo?: string | null;
}

export interface DroppedFile {
  facilityAccountId?: number | null; periodId?: number | null;
  sourceFilename?: string; currency?: string; content: string;
}

/** One drop zone: the server works out what the file is and routes it. */
export const dropEvidence = async (facilityId: number, drop: DroppedFile) =>
  (await axios.post<Record<string, any>>(`${base(facilityId)}/evidence`, drop)).data;

export const ingestBankEvidence = async (facilityId: number, payload: BankEvidencePayload) =>
  (await axios.post<Record<string, any>>(`${base(facilityId)}/bank-evidence`, payload)).data;

export const classifyBankLine = async (
  facilityId: number, transactionId: number, classification: string, reason: string) =>
  (await axios.post<Record<string, any>>(
    `${base(facilityId)}/bank-lines/${transactionId}/classify`, { classification, reason })).data;

export const recordServicerReport = async (facilityId: number, payload: Record<string, any>) =>
  (await axios.post<Record<string, any>>(`${base(facilityId)}/servicer-report`, payload)).data;

export interface WaterfallNode {
  id: number; priority_order: number; node_name: string; payment_type: string | null;
  mode: string | null; paying_agent_label: string | null; lender_facing: boolean;
  lender_class_key: string | null; source_clause: string | null;
}

export const getWaterfallNodes = async (facilityId: number): Promise<WaterfallNode[]> =>
  (await axios.get<{ nodes: WaterfallNode[] }>(`${base(facilityId)}/waterfall-nodes`)).data.nodes;

export const mapWaterfallNode = async (
  facilityId: number, body: { nodeName: string; payingAgentLabel: string; lenderFacing?: boolean }) =>
  (await axios.post<Record<string, any>>(`${base(facilityId)}/waterfall-nodes/map`, body)).data;

export const recordPayingAgentReport = async (facilityId: number, payload: Record<string, any>) =>
  (await axios.post<Record<string, any>>(`${base(facilityId)}/paying-agent-report`, payload)).data;

export const getMatching = async (facilityId: number, periodId: number) =>
  (await axios.get<Record<string, any>>(`${base(facilityId)}/periods/${periodId}/matching`)).data;

export const allocatePeriod = async (facilityId: number, periodId: number) =>
  (await axios.post<Record<string, any>>(`${base(facilityId)}/periods/${periodId}/allocate`)).data;

export const runStatement = async (facilityId: number, periodId: number) =>
  (await axios.post<Record<string, any>>(`${base(facilityId)}/periods/${periodId}/statement`)).data;

export const runPeriodWaterfall = async (facilityId: number, periodId: number) =>
  (await axios.post<Record<string, any>>(
    `${base(facilityId)}/periods/${periodId}/waterfall/run`)).data;

export const finalisePeriodWaterfall = async (
  facilityId: number, periodId: number, runId: number, notes?: string) =>
  (await axios.post<Record<string, any>>(
    `${base(facilityId)}/periods/${periodId}/waterfall/runs/${runId}/finalise`,
    { notes })).data;

export const runCashControl = async (facilityId: number, periodId: number) =>
  (await axios.post<Record<string, any>>(
    `${base(facilityId)}/periods/${periodId}/cash-control`)).data;

export interface LoanDrillDown {
  assetIdentifier: string; periodId: number;
  rollForward: Array<Record<string, any>>;
  remittanceLines: Array<Record<string, any>>;
  matchedCash: Array<Record<string, any>>;
  history: Array<Record<string, any>>;
  eligibilityImpact: Record<string, any>;
}

export const getLoanDrillDown = async (
  facilityId: number, periodId: number, assetIdentifier: string): Promise<LoanDrillDown> =>
  (await axios.get<LoanDrillDown>(
    `${base(facilityId)}/periods/${periodId}/loans/${encodeURIComponent(assetIdentifier)}`)).data;

export interface DispositionType {
  code: string; label: string; description: string; carries_forward: boolean;
}

export const getDispositionTypes = async (facilityId: number): Promise<DispositionType[]> =>
  (await axios.get<DispositionType[]>(`${base(facilityId)}/dispositions`)).data;

export const dispositionItem = async (
  facilityId: number, itemId: number,
  body: { disposition: string; notes: string; evidence?: Record<string, any>;
          blocksFunding?: boolean; revisionReason?: string }) =>
  (await axios.post<Record<string, any>>(
    `${base(facilityId)}/reconciling-items/${itemId}/disposition`, body)).data;

export const waiveDeliverable = async (facilityId: number, deliverableId: number, reason: string) =>
  (await axios.post<Record<string, any>>(
    `${base(facilityId)}/deliverables/${deliverableId}/waive`, { reason })).data;

/** F-LR-019: withdraw a bank file dropped by mistake; the server records it on Activity first. */
export const withdrawBankFile = async (facilityId: number, fileId: number, reason: string) =>
  (await axios.post<Record<string, any>>(
    `${base(facilityId)}/bank-files/${fileId}/withdraw`, { reason })).data;

export const getWaterfallCheck = async (facilityId: number, periodId: number) =>
  (await axios.get<Record<string, any>>(
    `${base(facilityId)}/periods/${periodId}/waterfall-check`)).data;

export const previewLenderCash = async (facilityId: number, periodId: number) =>
  (await axios.get<Record<string, any>>(
    `${base(facilityId)}/periods/${periodId}/lender-cash/preview`)).data;

export const bookLenderCash = async (facilityId: number, periodId: number) =>
  (await axios.post<Record<string, any>>(
    `${base(facilityId)}/periods/${periodId}/lender-cash`)).data;

export const reverseLenderCash = async (facilityId: number, bookingId: number, reason: string) =>
  (await axios.post<Record<string, any>>(
    `${base(facilityId)}/lender-cash/${bookingId}/reverse`, { reason })).data;

export const getSignoffReadiness = async (facilityId: number, periodId: number) =>
  (await axios.get<Record<string, any>>(`${base(facilityId)}/periods/${periodId}/signoff`)).data;

export const attestPeriod = async (facilityId: number, periodId: number, notes: string) =>
  (await axios.post<Record<string, any>>(
    `${base(facilityId)}/periods/${periodId}/signoff/attest`, { notes })).data;

export const countersignPeriod = async (facilityId: number, periodId: number, notes: string) =>
  (await axios.post<Record<string, any>>(
    `${base(facilityId)}/periods/${periodId}/signoff/countersign`, { notes })).data;

export const restatePeriod = async (facilityId: number, periodId: number, reason: string) =>
  (await axios.post<Record<string, any>>(
    `${base(facilityId)}/periods/${periodId}/restate`, { reason })).data;

export const evaluateAlerts = async (facilityId: number) =>
  (await axios.post<Record<string, any>>(`${base(facilityId)}/alerts/evaluate`)).data;

export { apiErrorMessage };

// ---------------------------------------------------------------- WS7: expected collections and the projected waterfall
export interface ExpectedCollectionsSummary extends Record<string, any> {
  facilityId: number; periodId: number; periodLabel: string; projected: boolean; note?: string;
  tape_ingest_id?: number; scenario_code?: string | null; loans?: number; opening_balance?: number; interest?: number; principal?: number;
  prepayments?: number; defaults?: number; recoveries?: number; total?: number; created_at?: string; created_by?: string; authority?: string; authorityBoundary?: string;
}
export const projectExpectedCollections = async (facilityId: number, periodId: number): Promise<ExpectedCollectionsSummary> =>
  (await axios.post<ExpectedCollectionsSummary>(`${base(facilityId)}/periods/${periodId}/expected-collections`)).data;
export const getExpectedCollections = async (facilityId: number, periodId: number): Promise<ExpectedCollectionsSummary> =>
  (await axios.get<ExpectedCollectionsSummary>(`${base(facilityId)}/periods/${periodId}/expected-collections`, { params: { _: Date.now() } })).data;
export const getExpectedCollectionLoans = async (facilityId: number, periodId: number, limit = 50): Promise<Array<Record<string, any>>> =>
  (await axios.get<Array<Record<string, any>>>(`${base(facilityId)}/periods/${periodId}/expected-collections/loans`, { params: { limit } })).data;
export const projectPeriodWaterfall = async (facilityId: number, periodId: number) =>
  (await axios.post<Record<string, any>>(`${base(facilityId)}/periods/${periodId}/waterfall/project`)).data;


export const refreshCollectionsPeriod = async (facilityId: number, periodId: number) =>
  (await axios.post<{ outcome: 'REFRESHED' | 'WAITING' | 'LOCKED'; message: string }>(
    `${base(facilityId)}/periods/${periodId}/refresh`)).data;

export const dispositionCase = async (facilityId: number, itemId: number, body: {
  disposition: string; notes: string; applyRelated: boolean; revisionReason?: string;
}) => (await axios.post<Record<string, any>>(`${base(facilityId)}/reconciling-items/${itemId}/case-disposition`, body)).data;
